import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, delay, put, race, select, spawn, take, call as typedCall } from 'typed-redux-saga';

import { resetConfigRoomId } from '../../../../../shared/features/config';
import { isMessengerIntegration, selectIsTeleMessenger } from '../../../../../shared/features/config/selectors';
import { feedbackSaga } from '../../../../../shared/features/feedback/sagas/feedbackSaga';
import { clearNotifications } from '../../../../../shared/features/notifications/ps-notifications';
import { getUserSelector } from '../../../../../shared/features/user';
import { selectIsUserB2BDomainMismatch } from '../../../../../shared/features/user/selectors';
import { getUrl, replace, SCREENS } from '../../../../../shared/helpers/navigation';
import { cloudApi } from '../../../../../shared/helpers/transport';
import { ConferenceStatus } from '../../../constants';
import {
    getConferenceConfig,
    getConferenceStatus,
    isChatAllowedInConference,
    isLocalRecordingActive,
} from '../../../selectors';
import { resetCall } from '../../../slice';
import { CancelCallActionParams } from '../../../types';
import { closeChatRecordNotification, exportChatHistorySavedAction, getChatAuthUserChanged } from '../../chat';
import { setBadNetworkQualityNotificationWasShown } from '../../network-quality';
import { recordHaveBeenSavedAction } from '../../record/slice';
import { disconnectConferenceAction } from '../slice';

const WAIT_RECORDING_SAVE_TIMEOUT = 10000;

export function* cancelCallSaga({ payload }: PayloadAction<CancelCallActionParams>) {
    yield put(clearNotifications());
    yield put(setBadNetworkQualityNotificationWasShown(false));
    const user = yield* select(getUserSelector);
    const chatAuthChanged = yield* select(getChatAuthUserChanged);
    const isChatAllowed = yield* select(isChatAllowedInConference);
    const isRecording = yield* select(isLocalRecordingActive);
    const isMessenger = yield* select(isMessengerIntegration);
    const isTeleMessenger = yield* select(selectIsTeleMessenger);
    const conferenceStatus = yield* select(getConferenceStatus);

    if (conferenceStatus === ConferenceStatus.WAITING_ROOM) {
        const conferenceConfig = yield* select(getConferenceConfig);
        if (conferenceConfig?.resolved_uri) {
            yield* typedCall([cloudApi, cloudApi.leaveWaitingRoom], conferenceConfig.resolved_uri);
        }
    }

    if (!user && chatAuthChanged && isChatAllowed) {
        // был перелогин в мессенджере
        // надо подхватить логин и показать главный экран  с рефрешем
        // фидбек в этом случае не должны показывать
        yield call(replace, SCREENS.CREATE_CALL, {});

        if (isRecording) {
            yield* all([take(recordHaveBeenSavedAction), take(exportChatHistorySavedAction)]);
            yield* take(closeChatRecordNotification);
        }

        window.location.reload();
    } else if (isMessenger || isTeleMessenger) {
        if (isRecording) {
            yield* all([take(recordHaveBeenSavedAction), take(exportChatHistorySavedAction)]);
        }

        yield put(disconnectConferenceAction({ source: payload.source }));
    } else {
        const isUserDomainMismatch = yield* select(selectIsUserB2BDomainMismatch);

        if (!payload?.skipFeedbackSaga && !isUserDomainMismatch) {
            yield spawn(feedbackSaga);
        }

        if (isRecording) {
            /** Не блокируем выход бесконечно: если сохранение зависло, продолжаем выход по таймауту */
            yield* race({
                saved: all([take(recordHaveBeenSavedAction), take(exportChatHistorySavedAction)]),
                timeout: delay(WAIT_RECORDING_SAVE_TIMEOUT),
            });
        }

        yield* put(resetCall());
        yield put(resetConfigRoomId());
        yield call(replace, SCREENS.CREATE_CALL, {});

        if (isUserDomainMismatch) {
            window.location.href = getUrl(SCREENS.CREATE_CALL, { searchString: window.location.search });
        }
    }
}
